import { selectCurrency } from 'legacy/lut/features/partner360/configuration'
import { Table } from 'legacy/lut/library/list'
import { Dispatch, Fragment, SetStateAction, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'
import { TplFunction } from 'core-components/library/list/types'
import { Static } from 'core-components/types'
import { Crud } from 'core/types'
import { P360Currency } from 'partner360/models/P360Currency'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../configs/translations'
import { isMissingOutcome } from '../../helpers/activityOutcome'
import { useMdfAnalysis } from '../../hooks/useMdfAnalysis'
import { MdfActivitiesTable } from '../mdfActivitiesTable/MdfActivitiesTable'

import { MdfActivitiesInitiativesGroupsTableFooter } from './footer/MdfActivitiesInitiativesGroupsTableFooter'
import { getColumns } from './helpers/getColumns'
import { getMdfInitiativeGroups } from './helpers/getMdfInitiativeGroups'
import { IMdfInitiativeGroupRow } from './model/types'

import styles from './MdfActivitiesInitiativesGroupsTable.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.dashboard.initiativesGroups

interface IMdfActivitiesInitiativesGroupsTableProps {
  partnerId: string
  expandedId: string | number | null
  setExpandedId: Dispatch<SetStateAction<string | number | null>>
  showOnlyPendingOutcome: boolean
  setShowOnlyPendingOutcome: (value: boolean) => void
}

export const MdfActivitiesInitiativesGroupsTable = ({
  partnerId,
  expandedId,
  setExpandedId,
  showOnlyPendingOutcome,
  setShowOnlyPendingOutcome,
}: IMdfActivitiesInitiativesGroupsTableProps) => {
  const activeCurrency = useSelector(selectCurrency)
  const isUsd = activeCurrency === P360Currency.USD

  const {
    filteredActivities: activities,
    initiatives,
    refetchInitiatives,
    isFetchingInitiatives,
    isFetchingMdfActivities,
  } = useMdfAnalysis(partnerId)
  const isFetching = isFetchingInitiatives || isFetchingMdfActivities

  const onToggleExpand = useCallback(
    (id: string | number) => setExpandedId((current) => (current === id ? null : id)),
    [setExpandedId]
  )

  const visibleActivities = useMemo(
    () => (showOnlyPendingOutcome ? activities.filter(isMissingOutcome) : activities),
    [activities, showOnlyPendingOutcome]
  )

  const { rows, total } = useMemo(
    () => getMdfInitiativeGroups(initiatives, visibleActivities, isUsd),
    [initiatives, visibleActivities, isUsd]
  )

  const source = useMemo(
    () =>
      new Crud<IMdfInitiativeGroupRow>({
        fetcher: new Static(rows),
        keyField: 'id',
        defaultPaging: {
          page: 0,
          pageSize: rows.length || 1,
        },
      }),
    [rows]
  )

  const columns = useMemo(
    () => getColumns({ isUsd, expandedId, onToggleExpand, isAllExpanded: showOnlyPendingOutcome }),
    [isUsd, expandedId, onToggleExpand, showOnlyPendingOutcome]
  )

  const rowTpl = useCallback<TplFunction<IMdfInitiativeGroupRow>>(
    (item, renderChildren, rowState, rowAttrs, additional, focusedId) => {
      const { key, testId, onClick, className } = rowAttrs
      return (
        <Fragment key={key}>
          <tr data-testid={testId} onClick={onClick} className={className}>
            {renderChildren(item, rowState, additional, focusedId, rowAttrs)}
          </tr>
          {(showOnlyPendingOutcome || expandedId === item.id) && (
            <tr className={styles.detailRow}>
              <td className={styles.detailCell} colSpan={columns.length}>
                <MdfActivitiesTable
                  activities={item.activities}
                  allActivities={activities}
                  initiatives={initiatives}
                  currency={activeCurrency as P360Currency}
                  refetchInitiatives={refetchInitiatives}
                />
              </td>
            </tr>
          )}
        </Fragment>
      )
    },
    [
      expandedId,
      showOnlyPendingOutcome,
      columns.length,
      initiatives,
      activities,
      refetchInitiatives,
      activeCurrency,
    ]
  )

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isFetching} />

      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h3 className={styles.title}>
            <Translation text={KEYS.title} span />
          </h3>
          <div className={styles.subtitle}>
            <Translation text={KEYS.subtitle} span />
          </div>
        </div>

        <Switch
          size="sm"
          checked={showOnlyPendingOutcome}
          onChange={setShowOnlyPendingOutcome}
          label={<Translation text={MDF_ACTIVITIES_TRANSLATION_KEYS.outcomes.onlyMissing} span />}
        />
      </div>

      <Table<IMdfInitiativeGroupRow>
        header
        source={source}
        columns={columns}
        rowTpl={rowTpl}
        footerTpl={() => <MdfActivitiesInitiativesGroupsTableFooter total={total} isUsd={isUsd} />}
      />
    </div>
  )
}

MdfActivitiesInitiativesGroupsTable.displayName = 'MdfActivitiesInitiativesGroupsTable'
